import type { SessionUser } from '@/types';

const API_ROOT = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
const browserAuthUrl = (path: string) => `${API_ROOT}/api/auth/browser${path}`;

interface BrowserSession {
  accessToken: string;
  user: SessionUser;
}

let accessToken: string | null = null;
let refreshRequest: Promise<BrowserSession | null> | null = null;

async function parseError(response: Response, fallback: string) {
  const payload = await response.json().catch(() => ({})) as { message?: string; error?: string };
  return payload.message ?? payload.error ?? fallback;
}

async function sessionRequest(path: string, body?: unknown, token?: string | null) {
  return fetch(browserAuthUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

export function getAccessToken() {
  return accessToken;
}

export async function refreshBrowserSession(): Promise<BrowserSession | null> {
  if (!refreshRequest) {
    refreshRequest = sessionRequest('/refresh').then(async (response) => {
      if (!response.ok) {
        accessToken = null;
        return null;
      }
      const session = await response.json() as BrowserSession;
      accessToken = session.accessToken;
      return session;
    }).catch(() => {
      accessToken = null;
      return null;
    }).finally(() => { refreshRequest = null; });
  }
  return refreshRequest;
}

export async function googleBrowserLogin(idToken: string) {
  const response = await sessionRequest('/google/login', { idToken });
  if (!response.ok) throw new Error(await parseError(response, 'Google sign-in failed'));
  const session = await response.json() as BrowserSession;
  accessToken = session.accessToken;
  return session;
}

export async function acceptGoogleInvite(idToken: string, token: string) {
  const response = await sessionRequest('/google/accept-invite', { idToken, token });
  if (!response.ok) throw new Error(await parseError(response, 'Invite activation failed'));
  const session = await response.json() as BrowserSession;
  accessToken = session.accessToken;
  return session;
}

export async function logoutBrowserSession() {
  const token = accessToken;
  accessToken = null;
  await sessionRequest('/logout', undefined, token).catch(() => undefined);
}

export { API_ROOT };
